const heartbeatIntervalMs = 10_000
const tabTtlMs = 25_000

const instances = new Map()

class TabCoordinator {
    constructor(channelName) {
        this.channelName = channelName
        this.tabId = TabCoordinator.createTabId()
        this.isTabVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
        this.localState = {}
        this.otherTabs = new Map()
        this.isDestroyed = false
        this.isInitialized = false
        this.leaveHandler = null
        this.heartbeatIntervalId = null
        this.channel = TabCoordinator.createChannel(channelName)

        this.visibilityHandler = () => {
            if (this.isDestroyed) {
                return
            }

            const isVisible = document.visibilityState === 'visible'
            if (this.isTabVisible !== isVisible) {
                this.isTabVisible = isVisible
                this.broadcast('visibility-change')
            }
        }
        document.addEventListener('visibilitychange', this.visibilityHandler)

        if (this.channel !== null) {
            this.channel.onmessage = (event) => {
                if (!event.data || this.isDestroyed) {
                    return
                }

                this.handleMessage(event.data)
            }
        }
    }

    static getInstance(channelName) {
        let instance = instances.get(channelName)
        if (!instance) {
            instance = new TabCoordinator(channelName)
            instances.set(channelName, instance)
        }

        return instance
    }

    static createTabId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID()
        }

        return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`
    }

    static createChannel(channelName) {
        if (typeof BroadcastChannel === 'undefined') {
            return null
        }

        return new BroadcastChannel(channelName)
    }

    init() {
        if (this.isDestroyed || this.isInitialized) {
            return
        }

        this.isInitialized = true

        this.leaveHandler = () => this.announceLeave()
        window.addEventListener('pagehide', this.leaveHandler)

        this.heartbeatIntervalId = window.setInterval(() => {
            this.broadcast('heartbeat')
        }, heartbeatIntervalMs)

        this.broadcast('tab-join')
    }

    handleMessage(message) {
        if (message.tabId === this.tabId) {
            return
        }

        switch (message.type) {
            case 'tab-join':
                this.rememberTab(message)
                this.broadcast('visibility-change')
                break
            case 'visibility-change':
            case 'state-change':
            case 'heartbeat':
                this.rememberTab(message)
                break
            case 'tab-leave':
                this.otherTabs.delete(message.tabId)
                break
        }
    }

    rememberTab(message) {
        if (typeof message.isVisible !== 'boolean') {
            return
        }

        this.otherTabs.set(message.tabId, {
            isVisible: message.isVisible,
            state: message.state ?? {},
            lastSeen: Date.now(),
        })
    }

    pruneStaleTabs() {
        const oldestAllowed = Date.now() - tabTtlMs
        for (const [tabId, tab] of this.otherTabs) {
            if (tab.lastSeen < oldestAllowed) {
                this.otherTabs.delete(tabId)
            }
        }
    }

    announceLeave() {
        this.post({ type: 'tab-leave', tabId: this.tabId })
    }

    broadcast(type) {
        this.post({
            type,
            tabId: this.tabId,
            isVisible: this.isTabVisible,
            state: { ...this.localState },
        })
    }

    post(message) {
        if (this.isDestroyed || this.channel === null) {
            return
        }

        this.channel.postMessage(message)
    }

    shouldPoll() {
        if (this.isDestroyed) {
            return false
        }

        if (this.isTabVisible) {
            return true
        }

        this.pruneStaleTabs()
        for (const tab of this.otherTabs.values()) {
            if (tab.isVisible) {
                return true
            }
        }

        return false
    }

    isVisible() {
        return this.isTabVisible
    }

    setLocalState(key, value) {
        if (this.isDestroyed || this.localState[key] === value) {
            return
        }

        this.localState[key] = value
        this.broadcast('state-change')
    }

    anyRemoteState(key, predicate = (value) => Boolean(value)) {
        if (this.isDestroyed) {
            return false
        }

        this.pruneStaleTabs()
        for (const tab of this.otherTabs.values()) {
            if (predicate(tab.state[key])) {
                return true
            }
        }

        return false
    }

    isLeader() {
        if (this.isDestroyed) {
            return false
        }

        this.pruneStaleTabs()
        for (const tabId of this.otherTabs.keys()) {
            if (tabId < this.tabId) {
                return false
            }
        }

        return true
    }

    destroy() {
        if (this.isDestroyed) {
            return
        }

        this.announceLeave()
        this.isDestroyed = true

        if (this.heartbeatIntervalId !== null) {
            window.clearInterval(this.heartbeatIntervalId)
            this.heartbeatIntervalId = null
        }

        if (this.leaveHandler) {
            window.removeEventListener('pagehide', this.leaveHandler)
            this.leaveHandler = null
        }

        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler)
            this.visibilityHandler = null
        }

        if (this.channel !== null) {
            this.channel.close()
            this.channel = null
        }

        this.otherTabs.clear()
        instances.delete(this.channelName)
    }
}

export const getTabCoordinator = (channelName) => {
    return TabCoordinator.getInstance(channelName)
}
